import { Badge } from '@ophelos/ui/components/badge';
import { Button } from '@ophelos/ui/components/button';
import { Card, CardContent } from '@ophelos/ui/components/card';
import { CalendarIcon, CheckCircle2, Copy } from 'lucide-react';
import { useState } from 'react';
import { useTheme } from '@/components/layout/ThemeProvider';
import { fromSubunits } from '@/lib/currency';
import type { BalanceReferenceDto } from '@/lib/connect-api';
import { useTranslation } from '@/hooks/useTranslation';

export interface BalanceReferenceCardProps {
  reference: BalanceReferenceDto | null;
  /** Drives the carve-out line explaining an amount smaller than the balance shown
   *  elsewhere on screen. */
  excludesArrangedDebts?: boolean;
}

/**
 * The account's current Multibanco code, so a code the customer was given stays
 * findable across reloads rather than only in the flow that issued it.
 *
 * Presentational: the owning screen reads via `useBalanceReference` once and passes
 * the result down, so a screen showing this twice cannot double-fetch, and a mint can
 * refresh it without a remount. Renders nothing when `reference` is null — no code, a
 * paid-off balance, the feature off, or a failed read all collapse to the same empty
 * render, so it can never show a stale code or an error the customer can't act on.
 */
export function BalanceReferenceCard({ reference, excludesArrangedDebts = false }: BalanceReferenceCardProps) {
  const { t, currentLanguage } = useTranslation();
  const { theme } = useTheme();
  const [copied, setCopied] = useState<'entity' | 'reference' | null>(null);

  if (!reference) return null;

  const logoSrc = theme === 'dark' ? '/assets/multibanco-dark_mode.svg' : '/assets/multibanco-light_mode.svg';

  const formattedAmount = new Intl.NumberFormat(currentLanguage, {
    style: 'currency',
    currency: reference.currency,
  }).format(fromSubunits(reference.amount_subunits));

  const formattedExpiry = reference.expires_at
    ? new Date(reference.expires_at).toLocaleDateString(currentLanguage, {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : null;

  const copy = async (value: string, field: 'entity' | 'reference') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(field);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // Clipboard unavailable (permissions, insecure context) — the code stays
      // on screen to copy by hand, so there is nothing to report.
    }
  };

  return (
    <Card className="py-4">
      <CardContent className="stack gap-3 px-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div className="shrink-0 size-10 rounded-full bg-card border border-border flex items-center justify-center">
              <img src={logoSrc} alt="Multibanco" className="h-6 w-auto" />
            </div>
            <div className="stack">
              <div className="text-sm font-semibold tracking-tight">{t('balanceReference.title')}</div>
              <p className="text-xs text-muted-foreground">{t('balanceReference.subtitle')}</p>
            </div>
          </div>
          <Badge variant="subtle">{formattedAmount}</Badge>
        </div>

        {/* One column on narrow screens — a 9-digit reference wraps mid-number
            in a half-width box, which is exactly the value being read out. */}
        <div className="grid grid-cols-1  gap-2.5">
          <div className="stack gap-1">
            <div className="text-sm">{t('multibanco.entity')}</div>
            <div className="flex items-center gap-1">
              <div className="flex-1 rounded-lg bg-soft border border-border-light px-2.5 py-1.5">
                <span className="text-base font-mono font-bold tracking-wider">{reference.entity}</span>
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => copy(reference.entity, 'entity')}
                title={t('multibanco.copyEntity')}
                aria-label={t('balanceReference.copyEntity')}
              >
                {copied === 'entity' ? <CheckCircle2 className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>

          <div className="stack gap-1">
            <div className="text-sm">{t('multibanco.reference')}</div>
            <div className="flex items-center gap-1">
              <div className="flex-1 rounded-lg bg-soft border border-border-light px-2.5 py-1.5">
                <span className="text-base font-mono font-bold tracking-wider">{reference.reference}</span>
              </div>
              <Button
                variant="ghost"
                size="icon-sm"
                onClick={() => copy(reference.reference, 'reference')}
                title={t('multibanco.copyReference')}
                aria-label={t('balanceReference.copyReference')}
              >
                {copied === 'reference' ? <CheckCircle2 className="size-4" /> : <Copy className="size-4" />}
              </Button>
            </div>
          </div>
        </div>

        <span aria-live="polite" className="sr-only">
          {copied === 'entity' ? t('balanceReference.copiedEntity') : copied === 'reference' ? t('balanceReference.copiedReference') : ''}
        </span>

        {formattedExpiry && (
          <div className="flex items-center gap-2 text-sm mt-1">
            <CalendarIcon className="size-4 text-muted-foreground" />
            <span>{t('balanceReference.validUntil', { date: formattedExpiry })}</span>
          </div>
        )}

        <p className="text-xs text-muted-foreground mt-2">{t('balanceReference.disclaimer')}</p>

        {/* The code deliberately leaves out debts already under a plan, so say so
            when that makes it smaller than the balance shown elsewhere on screen —
            otherwise the two numbers look like a mistake. */}
        {excludesArrangedDebts && <p className="text-xs text-muted-foreground">{t('balanceReference.excludesPlans')}</p>}
      </CardContent>
    </Card>
  );
}
